import type { ReactNode } from "react";
import { Link } from "react-router";

// Shared dashboard primitives so Home and the Save dashboard look identical.
// Extracted verbatim from HomePage so existing tiles render unchanged.

// One "+ …" quick-action line, shared by the hover-reveal overlays.
export const QUICK_ACTION_CLASS =
  "-mx-1 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[12px] text-ink/70 transition hover:bg-ink/[0.06] hover:text-accent";

export function QuickActionLink({
  to,
  children,
  icon = "+",
}: {
  to: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Link to={to} className={QUICK_ACTION_CLASS}>
      <span className="text-ink/40">{icon}</span>
      {children}
    </Link>
  );
}

// Compact dashboard tile: a KPI (number, optionally with a progress bar) that
// doubles as a quick jump. When `actions` are passed they stay collapsed and
// expand on hover / keyboard focus. The progress-bar row is always reserved so
// a tile WITH a bar isn't taller than one without, keeps tiles uniform.
//
// `to` is optional: without it the tile renders as a plain, non-interactive
// summary (no Link, no hover-lift, no bar row) - used by admin/overview pages
// that want the same label+value+sub shape but have no destination to jump
// to (TobisAdminPage, TobisMarketPage). Pass `compact` there too: a smaller
// tile (box-flat, no min-height, smaller value) that fits a KPI row nested
// inside an existing .box section instead of standing alone on the page
// background.
export function StatTile({
  to,
  label,
  value,
  sub,
  bar,
  loading,
  actions,
  compact = false,
}: {
  to?: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  bar?: number;
  loading?: boolean;
  actions?: ReactNode;
  compact?: boolean;
}) {
  const body = (
    <>
      <div className="text-[10px] tracking-wide text-ink/45">{label}</div>
      <div
        className={`mt-2 font-serif leading-none tabular-nums ${compact ? "text-2xl" : "text-3xl"}`}
      >
        {loading ? <span className="text-ink/30">…</span> : value}
      </div>
      {/* Always reserve the progress-bar row so a tile WITH a bar isn't taller
          than one without, keeps every tile a uniform height. Invisible when
          no bar is passed. Compact tiles skip the row entirely - none of
          their callers use a bar, and the row would just be dead space. */}
      {!compact && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full">
          {typeof bar === "number" ? (
            <div className="h-full w-full rounded-full bg-ink/10">
              <div className="h-full rounded-full bg-accent" style={{ width: `${bar}%` }} />
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  return (
    <div
      className={`group relative flex flex-col p-4 ${
        compact ? "box-flat bg-page" : "box min-h-32 bg-surface"
      } ${to ? "transition hover:-translate-y-0.5 hover:ring-ink/15" : ""}`}
    >
      {to ? (
        <Link to={to} className="flex flex-col">
          {body}
        </Link>
      ) : (
        <div className="flex flex-col">{body}</div>
      )}
      {/* sub sits OUTSIDE the tile link so it can carry its own inline links
          (nested <a> would be invalid). */}
      {sub ? <div className="mt-1.5 text-[11px] leading-snug text-ink/55">{sub}</div> : null}
      {actions ? (
        // Actions expand the tile inline on hover / keyboard focus: grid-rows
        // 0fr→1fr animates to the exact content height, so the tile grows to fit.
        <div className="grid grid-rows-[0fr] opacity-0 transition-all duration-500 ease-out group-hover:mt-3 group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:mt-3 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
          <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden">{actions}</div>
        </div>
      ) : null}
    </div>
  );
}
